import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  Grid,
  Checkbox,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  FileCopy,
  Delete,
  Warning,
  CheckCircle,
  Compare,
  DeleteSweep,
  Refresh,
  SwapHoriz,
  AccountBalance,
} from '@mui/icons-material';
import { transactionsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatCurrency, formatDate } from '../utils/helpers';
import LoadingButton from '../components/LoadingButton';

const Duplicates = () => {
  const { showSuccess, showError, showInfo } = useNotification();

  const [loading, setLoading] = useState(true);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [selectedForDeletion, setSelectedForDeletion] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, transactionIds: [] });

  useEffect(() => {
    loadDuplicates();
  }, []);

  const loadDuplicates = async () => {
    try {
      setLoading(true);
      const response = await transactionsApi.getDuplicates();
      setDuplicateGroups(response.data.duplicateGroups || []);
      
      if (response.data.duplicateGroups.length === 0) {
        showInfo('No duplicate transactions found');
      }
    } catch (error) {
      showError('Failed to load duplicate transactions');
      console.error('Error loading duplicates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectForDeletion = (transactionId) => {
    setSelectedForDeletion(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  const handleDeleteSelected = () => {
    if (selectedForDeletion.length === 0) {
      showError('Please select transactions to delete');
      return;
    }
    
    setDeleteDialog({
      open: true,
      transactionIds: selectedForDeletion
    });
  };

  const confirmDelete = async () => {
    try {
      await transactionsApi.bulkDelete(deleteDialog.transactionIds);
      showSuccess(`${deleteDialog.transactionIds.length} transactions deleted successfully`);
      
      // Remove deleted transactions from state
      setDuplicateGroups(prev => 
        prev.map(group => ({
          ...group,
          transactions: group.transactions.filter(
            t => !deleteDialog.transactionIds.includes(t.id)
          )
        })).filter(group => group.transactions.length > 1) // Keep only groups with more than 1 transaction
      );
      
      setSelectedForDeletion([]);
      setDeleteDialog({ open: false, transactionIds: [] });
    } catch (error) {
      showError('Failed to delete transactions');
    }
  };

  const handleSelectAllInGroup = (group) => {
    const transactionIds = group.transactions.map(t => t.id);
    // For auto-select, keep the first (oldest) one, select rest as duplicates
    const toSelect = transactionIds.slice(1);
    const allDuplicatesSelected = toSelect.every(id => selectedForDeletion.includes(id));
    
    if (allDuplicatesSelected) {
      // Deselect all duplicates in this group
      setSelectedForDeletion(prev => prev.filter(id => !toSelect.includes(id)));
    } else {
      // Select all duplicates (keep the first/oldest one)
      setSelectedForDeletion(prev => [...new Set([...prev, ...toSelect])]);
    }
  };

  // For refund pairs - select both to cancel them out
  const handleSelectRefundPair = (group) => {
    const transactionIds = group.transactions.map(t => t.id);
    const allSelected = transactionIds.every(id => selectedForDeletion.includes(id));
    
    if (allSelected) {
      // Deselect all
      setSelectedForDeletion(prev => prev.filter(id => !transactionIds.includes(id)));
    } else {
      // Select all (both expense and credit)
      setSelectedForDeletion(prev => [...new Set([...prev, ...transactionIds])]);
    }
  };

  const getTypeColor = (type) => {
    return type === 'credit' ? 'success' : 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <LoadingButton loading variant="outlined">
          Searching for duplicates...
        </LoadingButton>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Find Duplicate Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {duplicateGroups.length > 0 
              ? `Found ${duplicateGroups.length} potential duplicate groups`
              : 'No duplicate transactions found'
            }
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <LoadingButton
            variant="outlined"
            startIcon={<Refresh />}
            loading={loading}
            onClick={loadDuplicates}
          >
            Refresh
          </LoadingButton>
          {selectedForDeletion.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteSweep />}
              onClick={handleDeleteSelected}
            >
              Delete Selected ({selectedForDeletion.length})
            </Button>
          )}
        </Stack>
      </Box>

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>How it works:</strong> We find potential duplicates by comparing transactions with the same date, amount (absolute value), and account.
          This also catches <strong>expense/refund pairs</strong> - where the same amount appears as both expense and credit.
          Review each group carefully and decide which transactions to keep or delete.
        </Typography>
      </Alert>

      {duplicateGroups.length > 0 ? (
        <Grid container spacing={3}>
          {duplicateGroups.map((group, groupIndex) => (
            <Grid item xs={12} key={groupIndex}>
              <Card>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      {group.hasRefundPair ? (
                        <SwapHoriz color="info" />
                      ) : (
                        <FileCopy color="warning" />
                      )}
                      <Typography variant="h6">
                        {group.hasRefundPair ? 'Expense/Refund Pair' : 'Duplicate Group'} {groupIndex + 1}
                      </Typography>
                      <Chip 
                        label={`${group.transactions.length} transactions`} 
                        color={group.hasRefundPair ? 'info' : 'warning'}
                        size="small" 
                      />
                      {group.hasRefundPair && (
                        <Chip
                          label="Contains Credit & Expense"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  action={
                    group.hasRefundPair ? (
                      <Button
                        size="small"
                        color="info"
                        onClick={() => handleSelectRefundPair(group)}
                        startIcon={<SwapHoriz />}
                      >
                        Select both (cancel out)
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        onClick={() => handleSelectAllInGroup(group)}
                        startIcon={<CheckCircle />}
                      >
                        Auto-select duplicates
                      </Button>
                    )
                  }
                />
                <CardContent>
                  {/* Group Summary */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: group.hasRefundPair ? 'info.lighter' : 'grey.50', borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Date</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formatDate(group.date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Amount (Absolute)</Typography>
                        <Typography variant="h6">
                          {formatCurrency(group.amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Account</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AccountBalance fontSize="small" color="action" />
                          <Typography variant="body1">
                            {group.accountId || 'N/A'}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Bank</Typography>
                        <Typography variant="body1">
                          {group.bank || 'Unknown'}
                        </Typography>
                      </Grid>
                    </Grid>
                    {group.hasRefundPair && (
                      <Alert severity="info" sx={{ mt: 2 }} icon={<SwapHoriz />}>
                        <Typography variant="body2">
                          This group contains both expense and credit entries. This may be a <strong>refund</strong>. 
                          Consider deleting both to cancel them out.
                        </Typography>
                      </Alert>
                    )}
                  </Box>

                  {/* Transaction Comparisons */}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">Select to Delete</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Time / Added</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.transactions.map((transaction, index) => (
                          <TableRow 
                            key={transaction.id}
                            selected={selectedForDeletion.includes(transaction.id)}
                            sx={{
                              bgcolor: selectedForDeletion.includes(transaction.id) 
                                ? 'error.lighter' 
                                : (index === 0 && !group.hasRefundPair ? 'success.lighter' : 'inherit'),
                              opacity: selectedForDeletion.includes(transaction.id) ? 0.7 : 1
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedForDeletion.includes(transaction.id)}
                                onChange={() => handleSelectForDeletion(transaction.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip 
                                title={
                                  transaction.originalDescription ? (
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        Original Description:
                                      </Typography>
                                      <Typography variant="body2">
                                        {transaction.originalDescription}
                                      </Typography>
                                    </Box>
                                  ) : ''
                                } 
                                arrow 
                                placement="top"
                                disableHoverListener={!transaction.originalDescription}
                              >
                                <Typography 
                                  variant="body2"
                                  sx={{
                                    maxWidth: 250,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: transaction.originalDescription ? 'help' : 'default'
                                  }}
                                >
                                  {transaction.description}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={transaction.type} 
                                color={getTypeColor(transaction.type)} 
                                size="small" 
                              />
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                color={transaction.type === 'credit' ? 'success.main' : 'error.main'}
                                fontWeight="medium"
                              >
                                {transaction.type === 'credit' ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {transaction.category || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack>
                                {transaction.time && (
                                  <Typography variant="body2" fontWeight="medium">
                                    {transaction.time}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  Added: {formatDate(transaction.createdAt)}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {selectedForDeletion.includes(transaction.id) ? (
                                <Chip 
                                  label="Will be deleted" 
                                  color="error" 
                                  size="small"
                                  icon={<Delete />}
                                />
                              ) : index === 0 && !group.hasRefundPair ? (
                                <Chip 
                                  label="Keep (Oldest)" 
                                  color="success" 
                                  size="small"
                                  icon={<CheckCircle />}
                                />
                              ) : group.hasRefundPair ? (
                                <Chip 
                                  label={transaction.type === 'credit' ? 'Refund?' : 'Original?'} 
                                  color="info" 
                                  size="small"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip 
                                  label="Duplicate?" 
                                  color="warning" 
                                  size="small"
                                  icon={<Warning />}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No Duplicates Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Great! Your transaction data appears to be clean with no duplicate entries detected.
            </Typography>
            <Button
              variant="outlined"
              onClick={loadDuplicates}
              startIcon={<Refresh />}
            >
              Check Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, transactionIds: [] })}>
        <DialogTitle sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1, verticalAlign: 'middle' }} />
          Delete {deleteDialog.transactionIds.length} Transactions
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to delete {deleteDialog.transactionIds.length} selected transactions? 
            This will permanently remove them from your database.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, transactionIds: [] })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete Transactions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Duplicates;